/**
 * 수정제안(correction-suggestion) API wrapper (M15 / Phase 1).
 *
 * 백엔드 엔드포인트:
 *   POST /api/jobs/{job_id}/files/{file_id}/correction-requests
 *   GET  /api/jobs/{job_id}/files/{file_id}/correction-requests
 *   GET  /api/jobs/{job_id}/files/{file_id}/correction-requests/{request_id}
 *
 * 결과는 백엔드에서 ``Record.pending_extraction`` 에 staging 되므로, 채택/폐기는
 * 기존 record diff/apply/pending 흐름(``records.ts``)을 그대로 사용한다.
 */

import { currentApiLocale } from "@/lib/locale";

import { apiClient } from "./client";

/** 좌표는 PNG 자연 픽셀 (200 DPI, M11 BboxOverlay 와 동일 좌표계). */
export interface AnnotationBbox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 라벨(선택) + 값(필수) 쌍 1조. */
export interface AnnotationGroup {
  id: string;
  field: string;
  page: number;
  label_bbox?: AnnotationBbox | null;
  value_bbox: AnnotationBbox;
}

export interface CorrectionRequestPayload {
  feedback: string;
  annotations: AnnotationGroup[];
}

export type CorrectionStatus =
  | "pending"
  | "completed"
  | "completed_no_change"
  | "applied"
  | "discarded"
  | "failed";

/** POST 직후 큐잉 메타 (가벼운 응답). */
export interface CorrectionEnqueueOut {
  id: string;
  status: CorrectionStatus;
  created_at: string;
  celery_task_id: string | null;
  is_continuation_of: string | null;
}

/** 이력/상세 응답. */
export interface CorrectionRequestOut {
  id: string;
  file_id: string;
  user_id: number | null;
  feedback: string;
  annotations: AnnotationGroup[];
  annotations_hash: string;
  previous_request_id: string | null;
  conversation_messages: Array<Record<string, unknown>>;
  status: CorrectionStatus;
  llm_response: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export async function createCorrectionRequest(
  jobId: string,
  fileId: string,
  payload: CorrectionRequestPayload,
): Promise<CorrectionEnqueueOut> {
  const res = await apiClient.post<CorrectionEnqueueOut>(
    `/jobs/${jobId}/files/${fileId}/correction-requests`,
    payload,
    { params: { locale: currentApiLocale() } },
  );
  return res.data;
}

export async function listCorrectionRequests(
  jobId: string,
  fileId: string,
): Promise<CorrectionRequestOut[]> {
  const res = await apiClient.get<CorrectionRequestOut[]>(
    `/jobs/${jobId}/files/${fileId}/correction-requests`,
  );
  return res.data;
}

export async function getCorrectionRequest(
  jobId: string,
  fileId: string,
  requestId: string,
): Promise<CorrectionRequestOut> {
  const res = await apiClient.get<CorrectionRequestOut>(
    `/jobs/${jobId}/files/${fileId}/correction-requests/${requestId}`,
  );
  return res.data;
}

/** M21 #5 — 추천 질문 응답. */
export interface SuggestedQuestionsOut {
  questions: string[];
}

/**
 * 검수 의심사항(공란/저신뢰/AI 소견) 기반 추천 질문 생성 (kta-chat-vl).
 * 결과는 상태를 바꾸지 않는 read 성 계산이지만, LLM 호출이라 POST 로 노출.
 */
export async function getSuggestedQuestions(
  jobId: string,
  fileId: string,
): Promise<SuggestedQuestionsOut> {
  const res = await apiClient.post<SuggestedQuestionsOut>(
    `/jobs/${jobId}/files/${fileId}/suggested-questions`,
  );
  return res.data;
}
